/**
 * AthleteUserLink Service
 *
 * Manages the critical link between athlete profiles and Firebase user accounts.
 * This data is stored separately from athlete profiles to ensure the link is never
 * lost due to profile updates, syncing, or offline operations.
 *
 * Storage structure:
 * - athlete-user-link:{athleteId} → { firebaseUid, email, linkedAt }
 * - firebase:{firebaseUid} → athleteId (reverse lookup)
 */

import { kvBatch, kvGet } from "@/lib/storage/kv-client.ts";
import { BatchOperation } from "@/lib/storage/types.ts";

export interface AthleteUserLink {
  athleteId: string;
  firebaseUid: string;
  email: string;
  linkedAt: number;
}

export class AthleteUserLinkError extends Error {
  constructor(
    message: string,
    public code: "NOT_FOUND" | "ALREADY_LINKED" | "INVALID_OPERATION",
  ) {
    super(message);
    this.name = "AthleteUserLinkError";
  }
}

export class AthleteUserLinkService {
  /**
   * Create a new athlete-user link
   * This is called when an athlete signs up using an invitation link
   */
  static async createLink(
    athleteId: string,
    firebaseUid: string,
    email: string,
  ): Promise<AthleteUserLink> {
    // Check if athlete is already linked
    const existingLink = await this.getLinkByAthleteId(athleteId);
    if (existingLink) {
      throw new AthleteUserLinkError(
        "Athlete is already linked to a user account",
        "ALREADY_LINKED",
      );
    }

    // Check if firebaseUid is already linked to another athlete
    const existingAthleteId = await kvGet<string>(`firebase:${firebaseUid}`);
    if (existingAthleteId) {
      throw new AthleteUserLinkError(
        "This user account is already linked to another athlete",
        "ALREADY_LINKED",
      );
    }

    const link: AthleteUserLink = {
      athleteId,
      firebaseUid,
      email,
      linkedAt: Date.now(),
    };

    try {
      const batchOps: BatchOperation[] = [
        {
          type: "set",
          key: `athlete-user-link:${athleteId}`,
          value: link,
          syncMetadata: {
            type: "ATHLETE_USER_LINK",
            entityId: athleteId,
            userId: firebaseUid,
            createdBy: firebaseUid,
          },
        },
        {
          type: "set",
          key: `firebase:${firebaseUid}`,
          value: athleteId,
          syncMetadata: {
            type: "ATHLETE_USER_LINK",
            entityId: athleteId,
            userId: firebaseUid,
            createdBy: firebaseUid,
          },
        },
      ];

      await kvBatch(batchOps);
      return link;
    } catch (error) {
      console.error("Failed to create athlete-user link:", error);
      throw new AthleteUserLinkError(
        "Failed to create athlete-user link",
        "INVALID_OPERATION",
      );
    }
  }

  /**
   * Get athlete-user link by athlete ID
   */
  static async getLinkByAthleteId(
    athleteId: string,
  ): Promise<AthleteUserLink | null> {
    try {
      const link = await kvGet<AthleteUserLink>(
        `athlete-user-link:${athleteId}`,
      );
      return link;
    } catch (error) {
      console.error("Failed to get athlete-user link:", error);
      return null;
    }
  }

  /**
   * Get athlete-user link by Firebase UID
   */
  static async getLinkByFirebaseUid(
    firebaseUid: string,
  ): Promise<AthleteUserLink | null> {
    try {
      const athleteId = await kvGet<string>(`firebase:${firebaseUid}`);
      if (!athleteId) return null;

      return await this.getLinkByAthleteId(athleteId);
    } catch (error) {
      console.error("Failed to get athlete-user link by Firebase UID:", error);
      return null;
    }
  }

  /**
   * Get athlete ID by Firebase UID (reverse lookup)
   */
  static async getAthleteIdByFirebaseUid(
    firebaseUid: string,
  ): Promise<string | null> {
    try {
      const athleteId = await kvGet<string>(`firebase:${firebaseUid}`);
      return athleteId;
    } catch (error) {
      console.error("Failed to get athlete ID by Firebase UID:", error);
      return null;
    }
  }

  /**
   * Check if an athlete is linked to a user account
   */
  static async isAthleteLinked(athleteId: string): Promise<boolean> {
    const link = await this.getLinkByAthleteId(athleteId);
    return link !== null;
  }

  /**
   * Update the email for an existing link
   * This should be used sparingly - only when the user updates their email in Firebase Auth
   */
  static async updateEmail(
    athleteId: string,
    newEmail: string,
  ): Promise<AthleteUserLink> {
    const existingLink = await this.getLinkByAthleteId(athleteId);
    if (!existingLink) {
      throw new AthleteUserLinkError(
        "Athlete is not linked to any user account",
        "NOT_FOUND",
      );
    }

    const updatedLink: AthleteUserLink = {
      ...existingLink,
      email: newEmail,
    };

    try {
      await kvBatch([
        {
          type: "set",
          key: `athlete-user-link:${athleteId}`,
          value: updatedLink,
          syncMetadata: {
            type: "ATHLETE_USER_LINK",
            entityId: athleteId,
            userId: existingLink.firebaseUid,
            createdBy: existingLink.firebaseUid,
          },
        },
      ]);

      return updatedLink;
    } catch (error) {
      console.error("Failed to update athlete-user link email:", error);
      throw new AthleteUserLinkError(
        "Failed to update email",
        "INVALID_OPERATION",
      );
    }
  }

  /**
   * Delete an athlete-user link
   * This should be called when an athlete profile is deleted
   */
  static async deleteLink(athleteId: string): Promise<void> {
    const link = await this.getLinkByAthleteId(athleteId);
    if (!link) {
      // Link doesn't exist, nothing to delete
      return;
    }

    try {
      await kvBatch([
        {
          type: "del",
          key: `athlete-user-link:${athleteId}`,
        },
        {
          type: "del",
          key: `firebase:${link.firebaseUid}`,
        },
      ]);
    } catch (error) {
      console.error("Failed to delete athlete-user link:", error);
      throw new AthleteUserLinkError(
        "Failed to delete athlete-user link",
        "INVALID_OPERATION",
      );
    }
  }
}
